import type { TableColumnWidthOverride } from "_shared/_views/tables/table-column-fit";

/**
 * What the proposals table says about its columns' widths, over what the shared list-table fit
 * measures (#6708). The fit itself is `table-column-fit.ts`; this table used to have its own, which
 * chose Team's and Status's widths from the room the other columns left (#6512, #6600).
 */

export type TeamColumnDisplayMode = "minimal" | "compact" | "full";

/**
 * Each column's width before the table has been measured: the first paint, and any renderer with no
 * layout. Once measured, the fit sizes every column from its heading and content.
 *
 * Each is sized to what the column holds, so the default columns fit a 1280px window beside the
 * navigation (#6600). Negotiable holds only a ✓ or a ✕, under a short "Neg." header.
 */
export const proposalColumnSizes = {
  name: 140,
  rfp: 80,
  description: 200,
  creator: 120,
  negotiable: 56,
  // "Fee schedule" on one line, 86px measured, and the cell's padding.
  feeSchedule: 104,
  // "Due Mar 31, 2027" on one line, and the term on two.
  dates: 150,
  responseDueBy: 120,
  start: 95,
  end: 95,
  hireDate: 95,
  deleted: 100,
} as const satisfies Record<string, number>;

/** The narrowest the Dates column drags to: one whole date, "Mar 31, 2027", and the cell's padding. */
export const datesColumnMinSize = 140;

/** How wide the Team column has to be for each of the ways `VendorTeamLink` can draw a team. */
export const teamColumnSizes: Readonly<Record<TeamColumnDisplayMode, number>> = {
  minimal: 70,
  compact: 150,
  full: 260,
};

/**
 * The Team column's widths, stated rather than measured.
 *
 * A team draws more as it is given more room: its name in the compact form, and its leader as well in
 * the full one. Measured content would only ever be the form the column already has, so the column
 * asks for the full form and gives way to the minimal one.
 */
export const teamColumnWidth: TableColumnWidthOverride = {
  minimum: teamColumnSizes.minimal,
  preferred: teamColumnSizes.full,
};

/** The widest way to draw a team that a Team column of `width` has room for. */
export function teamDisplayModeFor(width: number): TeamColumnDisplayMode {
  if (width >= teamColumnSizes.full) return "full";
  if (width >= teamColumnSizes.compact) return "compact";
  return "minimal";
}

/**
 * The Status column's widths, stated rather than measured, since its cell wraps a long status onto two
 * lines and so measures as narrow as it is drawn.
 *
 * Roomy, a status reads on one line. 160px still holds the longest status ("Awaiting Team Leader
 * Approval") on its two lines: the longer line is under 124px, and the cell's padding takes it to
 * 156px. On a phone, 140px holds those lines with no more room to spare (#5907).
 *
 * An archived proposal only ever reads "Archived", "Canceled" or "Unknown", none wider than 68px, so
 * under the Archived chip the heading is the widest thing in the column, and the fit sizes it to that.
 */
export function statusColumnWidth(isArchivedView: boolean, isNarrowDisplay: boolean): TableColumnWidthOverride & {
  size: number;
  minSize: number;
} {
  if (isArchivedView) return { size: 100, minSize: 100 };
  if (isNarrowDisplay) return { size: 140, minSize: 140, minimum: 140, preferred: 140 };
  return { size: 220, minSize: 160, minimum: 160, preferred: 220 };
}

/** The checkbox column the Drafts tab adds for bulk delete. */
export const selectColumnSize = 60;

/**
 * The columns a reader sees until they choose otherwise, in the order the table draws them.
 *
 * Created by, Description, Negotiable and the three single-date columns are left out (#6512, #6711),
 * so the open list keeps to the six-column budget (`DEFAULT_DATA_COLUMN_BUDGET`). Each is still in the
 * table, under Show/Hide columns, and still answers its filter or sort. The creator and the description
 * read under the proposal's name, a non-negotiable proposal says so under its status, and the dates
 * share one Dates column, all while their own column is hidden.
 */
export const proposalColumnOrder = [
  "status",
  "name",
  "rfp",
  "description",
  "creator",
  "negotiable",
  "team",
  "feeSchedule",
  "dates",
  "responseDueBy",
  "start",
  "end",
  "hireDate",
  "deleted",
];
export const hiddenUntilChosen = ["description", "creator", "negotiable", "responseDueBy", "start", "end"] as const;

/** The columns only the Archived chip shows, and only by default there. */
export const archivedOnlyColumns = ["hireDate", "deleted"] as const;

/**
 * Where each column folds when the pane is short of room, and how soon: the lowest priority first
 * (#6711). Name, Status, Dates and Team never fold. Name and Dates are what a row is read by; Status
 * and Team are drawn narrow enough already.
 */
export const proposalColumnFolds = {
  description: { into: "name", priority: 1 },
  negotiable: { into: "status", priority: 1 },
  creator: { into: "name", priority: 2 },
  responseDueBy: { into: "dates", priority: 2 },
  start: { into: "dates", priority: 2 },
  end: { into: "dates", priority: 2 },
  hireDate: { into: "dates", priority: 3 },
  deleted: { into: "dates", priority: 3 },
  feeSchedule: { into: "name", priority: 4 },
  rfp: { into: "name", priority: 5 },
} as const satisfies Readonly<Record<string, { into: string; priority: number }>>;
